import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  TtsFlowComposeRequest,
  TtsFlowDetailResponse,
  TtsFlowListRow,
  TtsFlowUpdateRequest,
  TtsLogRequest,
  TtsLogRow,
} from './types';

export const ttsFlowKeys = {
  all: ['tts-flow'] as const,
  list: (userId: string | number | null) =>
    ['tts-flow', 'list', userId] as const,
  detail: (id: string | number) => ['tts-flow', 'detail', id] as const,
  logs: (userId: string | number | null, start: string, end: string) =>
    ['tts-flow', 'logs', userId, start, end] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ttsFlowKeys.all });
}

/** GET /api/obd/tts/flow/list/{userId} */
export function useTtsFlowList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ttsFlowKeys.list(userId),
    queryFn: () =>
      api.get<TtsFlowListRow[]>(`/api/obd/tts/flow/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsFlowListRow>(data),
  });
}

/** GET /api/obd/get/tts/flow/{ttsFlowId} */
export function useTtsFlowDetail(id: string | number | null) {
  return useQuery({
    queryKey: ttsFlowKeys.detail(id ?? 'none'),
    queryFn: () => api.get<TtsFlowDetailResponse>(`/api/obd/get/tts/flow/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** POST /api/obd/add/tts/flow */
export function useCreateTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TtsFlowComposeRequest) =>
      api.post<{ message?: string }>('/api/obd/add/tts/flow', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/update/tts/flow — same shape + `id`. */
export function useUpdateTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TtsFlowUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/update/tts/flow', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/get/continuous/logs — TTS campaign run logs.
 *  Body: `{ userId, startDate, endDate }` (YYYY-MM-DD). */
export function useTtsLogs(startDate: string, endDate: string) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ttsFlowKeys.logs(userId, startDate, endDate),
    queryFn: () =>
      api.post<TtsLogRow[]>('/api/obd/get/continuous/logs', {
        userId: Number(userId),
        startDate,
        endDate,
      } as TtsLogRequest),
    enabled: Boolean(userId && startDate && endDate),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsLogRow>(data),
  });
}

/** DELETE /api/obd/tts/flow/{id} — legacy uses POST-as-DELETE; mirror that. */
export function useDeleteTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message?: string }>(`/api/obd/tts/flow/${id}`),
    onSuccess: invalidate,
  });
}
